import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Gauge, Fuel, DollarSign, CalendarRange, MapPin, ShieldCheck, CircleAlert } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import CarGallery from './CarGallery';
import { listCarImages } from '../utils/carImages';

export default function CarInformationPage({ car, settings = {}, onBack }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const [images, setImages] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const imgs = await listCarImages(car?.id);
      if (alive) setImages(imgs);
    })();
    return () => { alive = false; };
  }, [car?.id]);

  const infoCards = useMemo(() => {
    const effectiveKm = (parseFloat(car?.km) || 0) + (parseFloat(car?.gpsOdometer) || 0);
    return [
      { label: isAr ? 'العلامة التجارية' : 'Marque', value: car?.brand || '—' },
      { label: isAr ? 'الموديل' : 'Modèle', value: car?.model || '—' },
      { label: isAr ? 'السنة' : 'Année', value: car?.year || '—' },
      { label: isAr ? 'اللوحة' : 'Plaque', value: car?.plate || '—' },
      { label: isAr ? 'الوقود' : 'Carburant', value: car?.fuel || '—' },
      { label: isAr ? 'المدينة' : 'Ville', value: car?.city || '—' },
      { label: isAr ? 'السعر اليومي' : 'Prix/jour', value: car?.dailyRate ? `${car.dailyRate} ${settings.currency || ''}` : '—' },
      { label: isAr ? 'العداد الكلي' : 'Km total', value: `${effectiveKm.toFixed(0)} km` },
      { label: isAr ? 'الحالة' : 'Statut', value: car?.status || '—' },
    ];
  }, [car, settings.currency, isAr]);

  if (!car) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
        {isAr ? 'لم يتم تحديد سيارة' : 'Aucune voiture sélectionnée'}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          {isAr ? 'عودة إلى السيارات' : 'Retour aux voitures'}
        </button>
        <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
          {isAr ? 'معلومات السيارة' : 'Informations véhicule'}
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <CarGallery
            images={images}
            fallback={car.photo}
            alt={`${car.brand} ${car.model}`}
            isAr={isAr}
          />
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">
              {isAr ? 'ملخص سريع' : 'Résumé'}
            </div>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">
              {`${car.brand || ''} ${car.model || ''}`.trim() || (isAr ? 'السيارة' : 'Véhicule')}
            </h1>
            <p className="text-sm text-gray-500">{car.plate || '—'}</p>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase text-gray-400">
                <Gauge className="h-3.5 w-3.5" /> {isAr ? 'العداد' : 'Compteur'}
              </div>
              <div className="mt-1 text-sm font-bold text-gray-900">
                {`${((parseFloat(car?.km) || 0) + (parseFloat(car?.gpsOdometer) || 0)).toFixed(0)} km`}
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase text-gray-400">
                <Fuel className="h-3.5 w-3.5" /> {isAr ? 'الوقود' : 'Carburant'}
              </div>
              <div className="mt-1 text-sm font-bold text-gray-900">{car?.fuel || '—'}</div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase text-gray-400">
                <DollarSign className="h-3.5 w-3.5" /> {isAr ? 'السعر' : 'Tarif'}
              </div>
              <div className="mt-1 text-sm font-bold text-gray-900">
                {car?.dailyRate ? `${car.dailyRate} ${settings.currency || ''}` : '—'}
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase text-gray-400">
                <MapPin className="h-3.5 w-3.5" /> {isAr ? 'المدينة' : 'Ville'}
              </div>
              <div className="mt-1 text-sm font-bold text-gray-900">{car?.city || '—'}</div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 bg-amber-50/60 p-3 text-xs text-amber-900">
            <div className="flex items-center gap-2 font-bold">
              <CircleAlert className="h-4 w-4" />
              {isAr ? 'ملاحظات' : 'Notes'}
            </div>
            <div className="mt-1">{car?.notes || (isAr ? 'لا توجد ملاحظات إضافية.' : 'Aucune note supplémentaire.')}</div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {infoCards.map((item) => (
          <div key={item.label} className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{item.label}</div>
            <div className="mt-1 text-sm font-bold text-gray-900">{item.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          {isAr ? 'معلومات الوثائق' : 'Informations documents'}
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {[
            ['taxExpiry', isAr ? 'ضريبة' : 'Taxe'],
            ['insuranceExpiry', isAr ? 'تأمين' : 'Assurance'],
            ['techVisitExpiry', isAr ? 'زيارة تقنية' : 'Visite technique'],
          ].map(([field, label]) => (
            <div key={field} className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm">
              <div className="font-semibold text-gray-500">{label}</div>
              <div className="mt-1 font-bold text-gray-900">{car?.[field] || '—'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
